import { useContext } from "react";

import { AppContext } from "components/store/source/";
import { Button, Modal, Text, useMedia } from "components/ui/source";

import { SearchAmenityFilter } from "types/graphql";

import { AmenitiesCheckBoxes } from "./AmenitiesCheckBoxes/AmenitiesCheckBoxes";
import { ApplyMobileFiltersButton } from "./ApplyMobileFiltersButton";
import { BathroomCounter } from "./BathroomCounter";
import { BedroomCounter } from "./BedroomCounter";
import { BedsCounter } from "./BedsCounter";
import { CircleIcon } from "./CircleIcon";
import { PriceRangeFilter } from "./PriceRangeFilter/PriceRangeFilter";
import { DestinationType } from "./SearchByPhrase/SearchByPhrase";
import { SearchByPhraseContainer } from "./SearchByPhrase/SearchByPhraseContainer";

import styles from "./MobileFiltersModal.module.scss";

type MobileFiltersModalProps = {
  l1Theme?: boolean;
  amenitiesData?: SearchAmenityFilter;
  pricingData?: Record<string, unknown>;
  bedBathCount?: number;
  neighborhoodData?: {
    neighborhoodFilter: {
      options: DestinationType[];
      selected: DestinationType;
    };
  };
  buildingName?: string;
};

export function MobileFiltersModal({
  l1Theme,
  pricingData,
  amenitiesData,
  bedBathCount,
  neighborhoodData,
  buildingName,
}: MobileFiltersModalProps) {
  const { mobileFiltersModalIsOpen, closeMobileFiltersModal } =
    useContext(AppContext);

  const isMobile = useMedia(["$mobile-lg", "$tablet-lg"], [true, true], false);

  const modalProps = {
    isOpen: mobileFiltersModalIsOpen,
    onRequestClose: closeMobileFiltersModal,
    contentLabel: "Modal showing search filters",
    className: styles.modal_content,
    overlayClassName: styles.modal_overlay,
  };

  const onClose = () => {
    closeMobileFiltersModal();
  };

  const closeFiltersButton = () => (
    <div className={styles.close}>
      <Button type="no_background" onClick={onClose}>
        <div className={styles.close_container}>
          <Text
            translateKey="search:modal.cancel"
            types={["caption1", "cancel"]}
          />
        </div>
      </Button>
    </div>
  );

  const ModalHeader = () => (
    <div className={styles.header}>
      {/** @ts-expect-error md is deprecated */}
      <Text types={["md"]} translateKey="search:modal.title" />
      {amenitiesData?.selected.length + bedBathCount > 0 && (
        <CircleIcon
          num={amenitiesData.selected.length + bedBathCount}
          className={styles.circle_number}
        />
      )}
    </div>
  );

  return (
    <Modal modalProps={modalProps}>
      <div className={styles.modal_container}>
        <div className={styles.header_row}>
          <ModalHeader />
          {closeFiltersButton()}
        </div>
        <div className={styles.filters_container}>
          <div className={styles.price_filter_row}>
            <PriceRangeFilter data={pricingData} isDropdown={false} />
          </div>
          {!l1Theme && (
            <>
              {isMobile && (
                <div className={styles.filter_row}>
                  <SearchByPhraseContainer
                    neighborhoodData={neighborhoodData}
                    buildingName={buildingName}
                  />
                </div>
              )}
              <div className={styles.filter_row}>
                <BedroomCounter mobile />
              </div>
              <div className={styles.filter_row}>
                <BedsCounter mobile />
              </div>
              <div className={styles.filter_row}>
                <BathroomCounter mobile />
              </div>
              <div className={styles.amenities_checkboxes}>
                <AmenitiesCheckBoxes data={amenitiesData} isMobile />
              </div>
            </>
          )}
        </div>
        <div className={styles.footer_row}>
          <ApplyMobileFiltersButton
            onClose={onClose}
            buttonText="search:modal.apply"
          />
        </div>
      </div>
    </Modal>
  );
}
